/**
 * Support Shape Components
 * Renders different shapes for Fixed (cube), Pinned (pyramid), and Roller (sphere) supports
 * Z-UP Coordinate System: X=East-West, Y=North-South, Z=Up-Down
 */

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { SupportType } from '../../types/support';

interface BaseSupportShapeProps {
  size: number;
  color: string;
  opacity: number;
  visible?: boolean;
}

/**
 * Fixed Support - Green Cube
 * Z-UP System: Cube is already orientation-independent (symmetrical in all axes)
 */
export const FixedSupportShape: React.FC<BaseSupportShapeProps> = ({
  size,
  color,
  opacity: _opacity, // Unused - using fixed 30% opacity
  visible = true
}) => {
  const cubeGeometry = useMemo(() => {
    return new THREE.BoxGeometry(size, size, size);
  }, [size]);

  const cubeMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.3, // Fixed 30% opacity for face
      side: THREE.DoubleSide
    });
  }, [color]);

  const wireframeMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: new THREE.Color(color).multiplyScalar(0.3), // Darker edges
      linewidth: 3, // Thicker lines for better visibility
      transparent: false,
      opacity: 1.0 // Full opacity for edges
    });
  }, [color]);

  const edgesGeometry = useMemo(() => {
    return new THREE.EdgesGeometry(cubeGeometry);
  }, [cubeGeometry]);

  if (!visible) return null;

  return (
    <group>
      {/* Main translucent cube */}
      <mesh geometry={cubeGeometry} material={cubeMaterial} />
      
      {/* Wireframe edges for better definition */}
      <lineSegments geometry={edgesGeometry} material={wireframeMaterial} />
    </group>
  );
};

/**
 * Pinned Support - 4-sided pyramid with connector line to node
 * Z-UP System: Pyramid base at node, tip points straight UP (+Z direction)
 */
export const PinnedSupportShape: React.FC<BaseSupportShapeProps> = ({
  size,
  color,
  opacity,
  visible = true
}) => {
  // Custom 4-sided pyramid geometry oriented with tip pointing +Z
  const pyramidGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    
    const baseRadius = size * 0.5;
    const height = size;
    
    // Define vertices: 4 base corners + 1 apex (tip)
    // Center the pyramid so it can be positioned correctly by parent
    // Base at Z=-height/2, Apex at Z=+height/2 (tip points up)
    const sqrt2_2 = Math.sqrt(2) / 2;
    const vertices = new Float32Array([
      // Base vertices (4 corners on XY plane)
      baseRadius * sqrt2_2, baseRadius * sqrt2_2, -height / 2,     // v0 - top-right
      -baseRadius * sqrt2_2, baseRadius * sqrt2_2, -height / 2,    // v1 - top-left
      -baseRadius * sqrt2_2, -baseRadius * sqrt2_2, -height / 2,   // v2 - bottom-left
      baseRadius * sqrt2_2, -baseRadius * sqrt2_2, -height / 2,    // v3 - bottom-right
      // Apex vertex (tip pointing up in +Z)
      0, 0, height / 2                                              // v4 - apex (tip)
    ]);
    
    // Define faces (triangles) using vertex indices
    const indices = new Uint16Array([
      // Base (2 triangles forming square)
      0, 2, 1,  // base triangle 1
      0, 3, 2,  // base triangle 2
      // Side faces (4 triangles connecting base to apex)
      0, 1, 4,  // side 1
      1, 2, 4,  // side 2
      2, 3, 4,  // side 3
      3, 0, 4   // side 4
    ]);
    
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.computeVertexNormals(); // For proper lighting/shading
    
    return geometry;
  }, [size]);

  const pyramidMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide
    });
  }, [color, opacity]);

  const wireframeMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: new THREE.Color(color).multiplyScalar(0.6),
      linewidth: 2
    });
  }, [color]);

  const edgesGeometry = useMemo(() => {
    return new THREE.EdgesGeometry(pyramidGeometry);
  }, [pyramidGeometry]);

  if (!visible) return null;

  return (
    <group>
      {/* 4-sided pyramid - centered, tip at +height/2, base at -height/2 */}
      <mesh 
        geometry={pyramidGeometry} 
        material={pyramidMaterial}
      />
      
      {/* Wireframe edges for pyramid */}
      <lineSegments 
        geometry={edgesGeometry} 
        material={wireframeMaterial}
      />
    </group>
  );
};

/**
 * Roller Support - Red sphere (rolls on horizontal XY plane)
 * Z-UP System: Sphere is orientation-independent, constrains vertical Z movement
 */
export const RollerSupportShape: React.FC<BaseSupportShapeProps> = ({
  size,
  color,
  opacity,
  visible = true
}) => {
  const sphereGeometry = useMemo(() => {
    return new THREE.SphereGeometry(size * 0.3, 16, 12);
  }, [size]);

  const sphereMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide
    });
  }, [color, opacity]);

  const wireframeMaterial = useMemo(() => {
    return new THREE.LineBasicMaterial({
      color: new THREE.Color(color).multiplyScalar(0.6),
      linewidth: 2
    });
  }, [color]);

  const edgesGeometry = useMemo(() => {
    return new THREE.EdgesGeometry(sphereGeometry);
  }, [sphereGeometry]);

  if (!visible) return null;

  return (
    <group>
      {/* Main translucent sphere */}
      <mesh geometry={sphereGeometry} material={sphereMaterial} />
      
      {/* Wireframe edges */}
      <lineSegments geometry={edgesGeometry} material={wireframeMaterial} />
    </group>
  );
};

interface SupportShapeProps {
  type: SupportType;
  size: number;
  color: string;
  opacity: number;
  visible?: boolean;
}

/**
 * Support Shape Selector - Renders appropriate shape based on support type
 */
export const SupportShape: React.FC<SupportShapeProps> = ({
  type,
  size,
  color,
  opacity,
  visible = true
}) => {
  if (!visible) return null;

  switch (type) {
    case 'fixed':
      return (
        <FixedSupportShape
          size={size}
          color={color}
          opacity={opacity}
          visible={visible}
        />
      );
    
    case 'pinned':
      return (
        <PinnedSupportShape
          size={size}
          color={color}
          opacity={opacity}
          visible={visible}
        />
      );
    
    case 'roller':
      return (
        <RollerSupportShape
          size={size}
          color={color}
          opacity={opacity}
          visible={visible}
        />
      );
    
    default:
      // Fallback to cube for unknown types
      return (
        <FixedSupportShape
          size={size}
          color={color}
          opacity={opacity}
          visible={visible}
        />
      );
  }
};

export default SupportShape;